import {
    ActionIcon,
    Alert,
    Anchor,
    Button,
    Checkbox,
    Group,
    Stack,
    TextInput,
    Title,
} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {RentalAssetFormModal} from './rental-asset-form-modal'
import {RentalAssetImportModal} from './rental-asset-import-modal'
import type {EquipmentStockSummaryListItem, RentalAsset} from './schemas'
import {useEquipmentStockSummaries} from './use-equipment-stock'
import {useRentalAssetTypes} from './use-rental-asset-types'
import {useCreateRentalAsset, useRentalAssets} from './use-rental-assets'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function filterUnitTrackedAssets(assets: RentalAsset[], nameFilter: string) {
    const unitTrackedAssets = assets.filter((asset) => asset.isUniquelyIdentified)
    const name = nameFilter.trim().toLowerCase()
    if (!name) {
        return unitTrackedAssets
    }
    return unitTrackedAssets.filter((asset) => (asset.name ?? '').toLowerCase().includes(name))
}

function compareRentalAssets(
    left: RentalAsset,
    right: RentalAsset,
    columnAccessor: DataTableSortStatus<RentalAsset>['columnAccessor'],
) {
    const leftValue = String(left[columnAccessor as keyof RentalAsset] ?? '')
    const rightValue = String(right[columnAccessor as keyof RentalAsset] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortRentalAssets(assets: RentalAsset[], sortStatus: DataTableSortStatus<RentalAsset>) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...assets].sort(
        (left, right) =>
            directionMultiplier * compareRentalAssets(left, right, sortStatus.columnAccessor),
    )
}

function compareStockSummaries(
    left: EquipmentStockSummaryListItem,
    right: EquipmentStockSummaryListItem,
    columnAccessor: DataTableSortStatus<EquipmentStockSummaryListItem>['columnAccessor'],
) {
    const leftValue = String(left[columnAccessor as keyof EquipmentStockSummaryListItem] ?? '')
    const rightValue = String(right[columnAccessor as keyof EquipmentStockSummaryListItem] ?? '')
    return leftValue.localeCompare(rightValue, undefined, {numeric: true})
}

function sortStockSummaries(
    summaries: EquipmentStockSummaryListItem[],
    sortStatus: DataTableSortStatus<EquipmentStockSummaryListItem>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...summaries].sort(
        (left, right) =>
            directionMultiplier * compareStockSummaries(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function RentalAssetsPage() {
    const navigate = useNavigate()
    const user = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [includeArchived, setIncludeArchived] = useState(false)
    const {data: rentalAssets = [], isLoading: assetsLoading} = useRentalAssets({includeArchived})
    const {data: stockSummariesData, isLoading: stockLoading} = useEquipmentStockSummaries()
    const {data: rentalAssetTypes = [], isLoading: typesLoading} = useRentalAssetTypes()
    const createRentalAsset = useCreateRentalAsset()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<RentalAsset>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [stockSortStatus, setStockSortStatus] = useState<
        DataTableSortStatus<EquipmentStockSummaryListItem>
    >({
        columnAccessor: 'typeName',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [importModalOpen, setImportModalOpen] = useState(false)

    const isLoading = assetsLoading || typesLoading || stockLoading
    const filtersActive = hasActiveFilters(debouncedName)
    const hasTypes = rentalAssetTypes.length > 0
    const stockSummaries = stockSummariesData?.summaries ?? []

    const sortedAssets = useMemo(
        () => sortRentalAssets(filterUnitTrackedAssets(rentalAssets, debouncedName), sortStatus),
        [rentalAssets, debouncedName, sortStatus],
    )

    const sortedStockSummaries = useMemo(
        () => sortStockSummaries(stockSummaries, stockSortStatus),
        [stockSummaries, stockSortStatus],
    )

    const equipmentColumns = useMemo((): DataTableColumn<RentalAsset>[] => {
        return [
            {
                accessor: 'name',
                title: 'Unit Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Unit name"
                        description="Show equipment whose unit names include the specified text"
                        placeholder="Search unit names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'rentalAssetTypeName',
                title: 'Type',
                sortable: true,
                render: ({rentalAssetTypeName}) => rentalAssetTypeName ?? '—',
            },
        ]
    }, [nameFilter])

    const stockColumns = useMemo((): DataTableColumn<EquipmentStockSummaryListItem>[] => {
        return [
            {
                accessor: 'typeName',
                title: 'Type',
                sortable: true,
            },
            {
                accessor: 'totalCount',
                title: 'Stock level',
                sortable: true,
            },
        ]
    }, [])

    return (
        <>
            {!hasTypes && !isLoading && (
                <Alert mb="md" title="No equipment types yet">
                    Create a type before adding equipment.{' '}
                    <Anchor component={Link} to="/rental-assets/types">
                        Go to Types
                    </Anchor>
                </Alert>
            )}

            <Group justify="flex-end" mb="md">
                {isAdmin(user) && (
                    <>
                        <Button variant="default" onClick={() => setImportModalOpen(true)}>
                            Import CSV
                        </Button>
                        <Button disabled={!hasTypes} onClick={() => setCreateModalOpen(true)}>
                            Create asset
                        </Button>
                    </>
                )}
            </Group>

            <Stack gap="xl">
                <Stack gap="sm">
                    <Group justify="space-between" align="center">
                        <Title order={3}>Equipment List</Title>
                        <Checkbox
                            label="Include archived"
                            checked={includeArchived}
                            onChange={(event) => setIncludeArchived(event.currentTarget.checked)}
                        />
                    </Group>
                    <DataTable
                        withTableBorder
                        withColumnBorders
                        striped
                        highlightOnHover
                        textSelectionDisabled
                        idAccessor="id"
                        records={sortedAssets}
                        columns={equipmentColumns}
                        fetching={isLoading}
                        minHeight={sortedAssets.length === 0 ? 180 : undefined}
                        noRecordsText={
                            filtersActive
                                ? 'No equipment matches your filters.'
                                : 'No unit-tracked equipment yet.'
                        }
                        sortStatus={sortStatus}
                        onSortStatusChange={setSortStatus}
                        onRowClick={({record}) =>
                            void navigate({
                                to: '/rental-assets/$assetId',
                                params: {assetId: record.id},
                            })
                        }
                    />
                </Stack>

                <Stack gap="sm">
                    <Title order={3}>Stock Levels</Title>
                    <DataTable
                        withTableBorder
                        withColumnBorders
                        striped
                        highlightOnHover
                        textSelectionDisabled
                        idAccessor="rentalAssetTypeId"
                        records={sortedStockSummaries}
                        columns={stockColumns}
                        fetching={isLoading}
                        minHeight={sortedStockSummaries.length === 0 ? 180 : undefined}
                        noRecordsText="No stock-tracked equipment types yet."
                        sortStatus={stockSortStatus}
                        onSortStatusChange={setStockSortStatus}
                        onRowClick={({record}) =>
                            void navigate({
                                to: '/rental-assets/stock/$typeId',
                                params: {typeId: record.rentalAssetTypeId},
                            })
                        }
                    />
                </Stack>
            </Stack>

            <RentalAssetFormModal
                opened={createModalOpen}
                rentalAssetTypes={rentalAssetTypes.filter((type) => type.isUniquelyIdentified)}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createRentalAsset.mutateAsync(input)}
                onSaved={() => setCreateModalOpen(false)}
            />

            <RentalAssetImportModal
                opened={importModalOpen}
                onClose={() => setImportModalOpen(false)}
            />
        </>
    )
}
